import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Camera, X, Loader2, Upload, Film, ChevronLeft, ChevronRight, Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const mediaSrc = (u) => (u ? (u.startsWith('http') ? u : `${BACKEND_URL}${u}`) : null);

async function downscale(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => resolve(b || file), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

function videoDuration(file) {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration || 0); };
    v.onerror = () => resolve(0);
    v.src = URL.createObjectURL(file);
  });
}

// ============ Media gallery (photos carousel + inline video) ============
export function MediaGallery({ photoUrls = [], videoUrl, cropName, postId, tall = false }) {
  const [idx, setIdx] = useState(0);
  const photos = photoUrls || [];
  const items = [...(videoUrl ? [{ type: 'video', url: videoUrl }] : []), ...photos.map((u) => ({ type: 'photo', url: u }))];
  if (items.length === 0) return null;
  const clamp = (n) => (n + items.length) % items.length;
  const cur = items[idx] || items[0];
  const h = tall ? 'h-[420px] sm:h-[520px]' : 'h-72 sm:h-80';

  return (
    <div className={`relative w-full ${h} rounded-2xl overflow-hidden border border-[#1A4331]/10 bg-black/5`} data-testid={`media-gallery-${postId || ''}`}>
      {cur.type === 'video' ? (
        <video src={mediaSrc(cur.url)} controls playsInline preload="metadata" className="w-full h-full object-contain bg-black" data-testid={`post-video-${postId || ''}`} />
      ) : (
        <img src={mediaSrc(cur.url)} alt={`${cropName || 'Harvest'}`} loading="lazy" className="w-full h-full object-cover" data-testid={`post-photo-${postId || ''}`} />
      )}
      {items.length > 1 && (
        <>
          <button onClick={() => setIdx(clamp(idx - 1))} aria-label="Previous" className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/45 hover:bg-black/65 text-white rounded-full p-1.5">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => setIdx(clamp(idx + 1))} aria-label="Next" className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/45 hover:bg-black/65 text-white rounded-full p-1.5">
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {items.map((it, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-white' : 'bg-white/50'}`} />
            ))}
          </div>
          <div className="absolute top-2 right-2 bg-black/50 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
            {idx + 1}/{items.length}
          </div>
        </>
      )}
      {cur.type === 'video' && items.length === 1 && (
        <div className="absolute top-2 left-2 bg-[#1A4331]/85 text-[#FAF6EC] text-[11px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1"><Play className="w-3 h-3" /> Video</div>
      )}
    </div>
  );
}

// ============ Media picker (up to 5 photos + one ≤30s video) ============
export function MediaPicker({ photos, setPhotos, video, setVideo, maxPhotos = 5, allowVideo = true }) {
  const photoRef = useRef(null);
  const cameraRef = useRef(null);
  const videoRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setError('');
    setUploading(true);
    try {
      const room = maxPhotos - photos.length;
      for (const file of files.slice(0, room)) {
        const blob = await downscale(file);
        const fd = new FormData();
        fd.append('file', blob, 'harvest.jpg');
        const r = await axios.post(`${API}/community/upload-photo`, fd, { withCredentials: true });
        setPhotos((prev) => (prev.length < maxPhotos ? [...prev, r.data.photo_url] : prev));
      }
    } catch (err) { setError('Photo upload failed — please try again'); }
    finally { setUploading(false); }
  };

  const addVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    const dur = await videoDuration(file);
    if (dur && dur > 31) { setError('Video must be 30 seconds or less'); return; }
    if (file.size > 60 * 1024 * 1024) { setError('Video too large (max 60MB)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name || 'clip.mp4');
      const r = await axios.post(`${API}/community/upload-video`, fd, { withCredentials: true });
      setVideo(r.data.video_url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Video upload failed — please try again');
    } finally { setUploading(false); }
  };

  return (
    <div>
      <input ref={photoRef} type="file" accept="image/*" multiple onChange={addPhotos} className="hidden" data-testid="media-photo-input" />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={addPhotos} className="hidden" data-testid="media-camera-input" />
      <input ref={videoRef} type="file" accept="video/*" onChange={addVideo} className="hidden" data-testid="media-video-input" />

      {(photos.length > 0 || video) && (
        <div className="grid grid-cols-3 gap-2 mb-2">
          {video && (
            <div className="relative rounded-xl overflow-hidden border border-[#1A4331]/10 aspect-square bg-black">
              <video src={mediaSrc(video)} className="w-full h-full object-cover" muted playsInline />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Play className="w-6 h-6 text-white/90" /></div>
              <button type="button" onClick={() => setVideo(null)} data-testid="remove-video-button" className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          {photos.map((u, i) => (
            <div key={u} className="relative rounded-xl overflow-hidden border border-[#1A4331]/10 aspect-square">
              <img src={mediaSrc(u)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setPhotos(photos.filter((x) => x !== u))} data-testid={`remove-photo-${i}`} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button type="button" onClick={() => photoRef.current?.click()} disabled={uploading || photos.length >= maxPhotos} data-testid="media-add-photo"
          className="flex-1 min-w-[100px] border-2 border-dashed border-[#8EAA97]/60 hover:border-[#1A4331] rounded-xl py-3 flex items-center justify-center gap-2 text-sm font-semibold text-[#1A4331] disabled:opacity-40 transition-colors">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Photos {photos.length}/{maxPhotos}
        </button>
        <button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading || photos.length >= maxPhotos} data-testid="media-camera"
          className="border-2 border-dashed border-[#8EAA97]/60 hover:border-[#1A4331] rounded-xl px-4 py-3 flex items-center justify-center gap-2 text-sm font-semibold text-[#1A4331] disabled:opacity-40 transition-colors">
          <Camera className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => videoRef.current?.click()} disabled={uploading || !!video} data-testid="media-add-video"
          className={`flex-1 min-w-[100px] border-2 border-dashed border-[#8EAA97]/60 hover:border-[#1A4331] rounded-xl py-3 flex items-center justify-center gap-2 text-sm font-semibold text-[#1A4331] disabled:opacity-40 transition-colors ${allowVideo ? '' : 'hidden'}`}>
          <Film className="w-4 h-4" /> {video ? 'Video added' : 'Video ≤30s'}
        </button>
      </div>
      {error && <p className="text-xs text-[#C66B4A] mt-1.5" data-testid="media-error">{error}</p>}
    </div>
  );
}
